import { useField } from 'formik';
import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Button } from '@/components/Button/Button';
import type { InputType } from '@/components/Input/Input';
import { Input } from '@/components/Input/Input';
import { InputAdornment } from '@/components/InputAdornment/InputAdornment';
import { useForkRef } from '@/hooks/useForkRef';
import type { FormikFieldValiadtionType } from '@/types';

type PasswordFieldType = FormikFieldValiadtionType &
  Omit<InputType, 'name'> &
  Required<Pick<InputType, 'name'>> & { hidePasswordHint?: boolean };

const StyledButton = styled(Button)`
  ${tw`pointer-events-auto p-2`}
`;

const StyledInput = styled(Input)(({ error }) => [
  !error &&
    tw`focus:outline-none focus:ring focus:ring-emerald-100  focus:border-emerald-900`,
]);

const StyledInputAdornment = styled(InputAdornment)`
  ${tw`pr-2`}
`;

{
  /* @todo: Revisit src/frontend/src/components/AuthV2/SignupForm/index.tsx
   * Password helper text UI
   */
}
export const PasswordField = React.forwardRef<
  HTMLInputElement,
  PasswordFieldType
>(({ name, hidePasswordHint, validate, ...rest }, ref) => {
  const [field, meta] = useField({ name, validate });

  const [showPassword, setShowPassword] = React.useState(false);
  const internalRef = React.useRef<HTMLElement>(null);

  const combinedRef = useForkRef(internalRef, ref);

  const errorText =
    meta.error && (process.env.NODE_ENV === 'test' ? true : meta.touched)
      ? meta.error
      : '';

  return (
    <StyledInput
      ref={combinedRef as React.Ref<HTMLInputElement>}
      labelHidden
      error={!!errorText}
      helperText={errorText}
      type={showPassword ? 'text' : 'password'}
      endAndornment={
        <StyledInputAdornment>
          <StyledButton
            label={!showPassword ? 'Show' : 'Hide'}
            size='small'
            variant='tertiary'
            onClick={() => setShowPassword((state) => !state)}
          />
        </StyledInputAdornment>
      }
      {...field}
      {...rest}
      name={name}
    />
  );
});
